import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useLocation } from 'react-router';
import {
    login as loginApi,
    register as registerApi,
    logout as logoutApi,
    getProfile,
    updateProfile as updateProfileApi,
    forgotPassword as forgotPasswordApi,
    resetPassword as resetPasswordApi
} from '@/api/auth';
import { useAuthStore } from '@/stores/useAuthStore';
import { useCartStore } from '@/stores/useCartStore';
import { toast } from '@/stores/useToastStore';
import { useMergeCart, cartKeys } from '@/hooks/useCart';
import { getSessionId, clearSessionId } from '@/hooks/useSession';
import { CustomerLoginRequest, CustomerRegisterRequest, CustomerUpdateProfileRequest } from '@/types';

export const useAuth = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const queryClient = useQueryClient();
    const { setAuth, logout: logoutStore, updateUser } = useAuthStore();
    const { clearCart } = useCartStore();
    const mergeCart = useMergeCart();

    // Get the return URL from location state. ProtectedRoute (and the checkout
    // "Sign in" link) set `from`; tolerate a legacy `returnTo` key too so a
    // post-login redirect is never silently dropped.
    const returnState = location.state as { from?: string; returnTo?: string } | null;
    const returnUrl = returnState?.from || returnState?.returnTo || '/';

    const loginMutation = useMutation({
        mutationFn: (data: CustomerLoginRequest) => loginApi(data),
        onSuccess: async (data, variables) => {
            // Persist the user's Remember Me choice alongside the tokens. When it's false
            // the auth store routes the persisted blob to sessionStorage so it's cleared on
            // browser close; when true (or unset) it stays in localStorage for ~30 days.
            setAuth(
                data.customer,
                data.accessToken,
                data.refreshToken,
                data.expiresAt,
                variables.rememberMe ?? false
            );

            // Merge guest cart with user cart after login
            const sessionId = getSessionId();
            if (sessionId) {
                try {
                    await mergeCart.mutateAsync({ guestSessionId: sessionId });
                    clearSessionId(); // Clear guest session after merge
                    // Invalidate cart queries to refresh
                    queryClient.invalidateQueries({ queryKey: cartKeys.all });
                } catch {
                    // Show non-blocking warning while login still succeeds.
                    toast.warning('Your session items could not be transferred to your cart. Please add them again.');
                }
            }

            // Navigate to the return URL (where user was trying to go)
            navigate(returnUrl, { replace: true });
        },
    });

    const registerMutation = useMutation({
        mutationFn: (data: CustomerRegisterRequest) => registerApi(data),
        onSuccess: async (data) => {
            // ✅ Update: Store all tokens including refresh token
            setAuth(data.customer, data.accessToken, data.refreshToken, data.expiresAt);

            // Merge guest cart with user cart after registration
            const sessionId = getSessionId();
            if (sessionId) {
                try {
                    await mergeCart.mutateAsync({ guestSessionId: sessionId });
                    clearSessionId(); // Clear guest session after merge
                    // Invalidate cart queries to refresh
                    queryClient.invalidateQueries({ queryKey: cartKeys.all });
                } catch {
                    // Show non-blocking warning while registration still succeeds.
                    toast.warning('Your session items could not be transferred to your cart. Please add them again.');
                }
            }

            // Navigate to the return URL (where user was trying to go)
            navigate(returnUrl, { replace: true });
        },
    });

    const logoutMutation = useMutation({
        mutationFn: logoutApi,
        onSettled: () => {
            // Clear auth state
            logoutStore();
            // Clear cart state (prevents next user seeing previous user's items)
            clearCart();
            // Clear all query cache
            queryClient.clear();
            navigate('/login');
        },
    });

    const updateProfileMutation = useMutation({
        mutationFn: (data: CustomerUpdateProfileRequest) => updateProfileApi(data),
        onSuccess: (data) => {
            updateUser(data);
            queryClient.invalidateQueries({ queryKey: ['profile'] });
        },
    });

    const forgotPasswordMutation = useMutation({
        mutationFn: forgotPasswordApi,
    });

    const resetPasswordMutation = useMutation({
        mutationFn: resetPasswordApi,
    });

    return {
        login: loginMutation,
        register: registerMutation,
        logout: logoutMutation,
        updateProfile: updateProfileMutation,
        forgotPassword: forgotPasswordMutation,
        resetPassword: resetPasswordMutation,
    };
};

export const useProfile = () => {
    const { isAuthenticated } = useAuthStore();

    return useQuery({
        queryKey: ['profile'],
        queryFn: getProfile,
        enabled: isAuthenticated,
    });
};
